/**
 * FP3-035B — Display view for a completed articulation screener.
 *
 * Meeting-friendly screen view: same body as the print view but
 * wrapped in DisplayShell (slim top bar with Print + Exit display).
 * Reuses ArticulationScreenerResultPanel so display + print +
 * inline-dialog never drift.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import {
  DisplayShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";
import ArticulationScreenerResultPanel from "@/components/student/ArticulationScreenerResultPanel";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function DisplayAssessmentRun() {
  const { studentId, runId } = useParams();
  const [student, setStudent] = useState(null);
  const [run, setRun] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getStudent(studentId),
      api.getAssessmentRun(studentId, runId),
    ])
      .then(([sResp, rResp]) => {
        if (cancelled) return;
        setStudent(sResp.data);
        setRun(rResp.data);
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, runId]);

  const name = student ? `${student.first_name} ${student.last_name}` : "Student";
  const ageStamp = run?.student_age_at_assessment?.label;
  const version =
    run?.screener_template_id === "ages_3_5"
      ? "Ages 3-5"
      : run?.screener_template_id === "ages_5_8"
      ? "Ages 5-8"
      : run?.title || "Articulation Screener";

  return (
    <DisplayShell
      title="Articulation Screener Result"
      subtitle={
        student && run
          ? `${name} · ${version} · ${fmtDate(run.date_administered)}${ageStamp ? ` · Age ${ageStamp}` : ""}`
          : ""
      }
      testId="display-assessment-run"
    >
      <PrintAccessGate phase={phase} error={error}>
        {student && run && (
          <div className="px-8 py-6 max-w-5xl mx-auto" data-testid="display-assessment-run-body">
            <ArticulationScreenerResultPanel
              run={run}
              student={student}
              hideInterpretationActions
            />
          </div>
        )}
      </PrintAccessGate>
    </DisplayShell>
  );
}
